import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import Button from "../../components/Button.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {getTranslatedPlanName} from "../project/Subscription/PlanName.jsx";
import EChart from "./ReportingEChart.jsx";
import {SortableHeader, TablePagination, useSortableRows} from "./ReportingLab.jsx";

const DEFAULT_PAGE_SIZE = 25;

function paymentSortValue(row, key) {
    if (key === "paidAt") return new Date(row.paidAt ?? row.expectedAt).getTime();
    if (key === "project") return row.projectName;
    if (key === "package") return row.packageCode;
    if (key === "amount") return row.totalAmount ?? (row.amount == null ? null : row.amount * 100);
    if (key === "payment") return row.paymentStatus;
    if (key === "invoice") return row.invoiceStatus;
    return null;
}

export function formatMinorMoney(amount, currency) {
    if (amount == null) return "—";

    const formatter = new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: currency || "CZK",
    });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;

    return new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: currency || "CZK",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format((amount ?? 0) / 100);
}

function PaymentAmounts({row, t}) {
    if (row.totalAmount == null && row.netAmount == null && row.amount != null) {
        const predatesVatRegistration = row.financialDataStatus === "pre_vat_registration_amount_unverified";

        return <div className="space-y-1 text-right">
            <div className="font-semibold text-slate-900 dark:text-white">
                <span className="mr-2 text-xs font-normal text-slate-500 dark:text-slate-400">{predatesVatRegistration
                    ? t("admin.subscription_payments.legacy_amount_label", {defaultValue: "Cena balíčku bez DPH"})
                    : t("admin.subscription_payments.unverified_amount_label", {defaultValue: "Cena balíčku"})}</span>
                {formatMajorMoney(row.amount, row.currency)}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
                {predatesVatRegistration
                    ? t("admin.subscription_payments.legacy_amount_note", {defaultValue: "Platba proběhla před registrací k DPH; DPH se neuplatňovala."})
                    : t("admin.subscription_payments.unverified_amount_note", {defaultValue: "Přesná stržená částka ještě není ověřená v ledgeru."})}
            </div>
        </div>;
    }

    return <div className="space-y-1 text-right">
        <div className="font-semibold text-slate-900 dark:text-white">
            <span className="mr-2 text-xs font-normal text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.gross_amount", {defaultValue: "Celkem s DPH"})}</span>
            {formatMinorMoney(row.totalAmount, row.currencyCode)}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
            {t("admin.subscription_payments.net_amount", {defaultValue: "Bez DPH"})} {formatMinorMoney(row.netAmount, row.currencyCode)}
        </div>
        {row.currencyCode !== "CZK" && row.totalAmountCzk != null && <div className="text-xs text-slate-500 dark:text-slate-400">
            {t("admin.subscription_payments.cnb_conversion", {defaultValue: "Přepočet ČNB"})} {formatMinorMoney(row.totalAmountCzk, "CZK")}
        </div>}
    </div>;
}

function formatDate(value) {
    return value ? new Intl.DateTimeFormat("cs-CZ", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value)) : "—";
}

function SummaryCard({label, value, tone = "slate", helper = null}) {
    const tones = {
        slate: "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800",
        green: "border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-950/20",
        red: "border-rose-200 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-950/20",
        amber: "border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/20",
        violet: "border-violet-200 bg-violet-50/60 dark:border-violet-800 dark:bg-violet-950/20",
    };

    return <div className={`rounded-2xl border p-4 shadow-sm ${tones[tone]}`}>
        <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{value}</div>
        {helper && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</div>}
    </div>;
}

function formatMajorMoney(amount, currency, compact = false) {
    return new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: currency || "CZK",
        notation: compact ? "compact" : "standard",
        maximumFractionDigits: compact ? 1 : 2,
    }).format(Number(amount ?? 0));
}

function formatMonthLabel(month) {
    const [year, monthNumber] = String(month).split("-").map(Number);
    if (!year || !monthNumber) return month;

    return new Intl.DateTimeFormat("cs-CZ", {month: "short", year: "2-digit"})
        .format(new Date(year, monthNumber - 1, 1));
}

export function buildRevenueTrendChartOption(stat, t) {
    const currency = stat.currencyCode || "CZK";
    const collectedLabel = t("admin.subscription_payments.financial_chart_collected", {defaultValue: "Vybráno"});
    const pendingLabel = t("admin.subscription_payments.financial_chart_pending", {defaultValue: "Nadcházející"});
    const monthlyAmounts = stat.monthlyNetAmounts ?? [];
    const pendingData = monthlyAmounts.map((_, index) => index === monthlyAmounts.length - 1
        ? (stat.currentMonthPendingNetAmount ?? 0) / 100
        : 0);

    return {
        color: ["#10b981", "#f59e0b"],
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            valueFormatter: (value) => formatMajorMoney(value, currency),
        },
        legend: {data: [collectedLabel, pendingLabel], bottom: 0, left: "center"},
        grid: {left: 12, right: 12, top: 24, bottom: 52, containLabel: true},
        xAxis: {
            type: "category",
            data: monthlyAmounts.map((item) => formatMonthLabel(item.month)),
            axisTick: {show: false},
        },
        yAxis: {
            type: "value",
            axisLabel: {formatter: (value) => formatMajorMoney(value, currency, true)},
            splitLine: {lineStyle: {color: "#e2e8f0", type: "dashed"}},
        },
        series: [
            {
                name: collectedLabel,
                type: "bar",
                stack: "net",
                barMaxWidth: 42,
                data: monthlyAmounts.map((item) => (item.invoicedNetAmount ?? 0) / 100),
            },
            {
                name: pendingLabel,
                type: "bar",
                stack: "net",
                barMaxWidth: 42,
                data: pendingData,
            },
        ],
    };
}

function FinancialStatSwitch({stats, selectedStat, onSelect, t}) {
    if (stats.length <= 1) return null;

    return <div className="inline-flex self-start rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-900/40">
        {stats.map((stat) => <button key={financialStatKey(stat)} type="button" onClick={() => onSelect(financialStatKey(stat))} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${financialStatKey(selectedStat) === financialStatKey(stat) ? "bg-violet-600 text-white shadow-sm" : "text-slate-600 hover:text-violet-700 dark:text-slate-300"}`}>{financialStatLabel(stat, t)}</button>)}
    </div>;
}

function FinancialStats({stats, ossStats, t}) {
    const [selectedStatKey, setSelectedStatKey] = useState(null);
    if (stats.length === 0) return null;
    const preferredStat = stats.find((stat) => stat.allCurrenciesConvertedToCzk) ?? stats[0];
    const selectedStat = stats.find((stat) => financialStatKey(stat) === selectedStatKey) ?? preferredStat;
    const projectedNetAmount = (selectedStat.currentMonthInvoicedNetAmount ?? 0) + (selectedStat.currentMonthPendingNetAmount ?? 0);

    return <section>
        <div className="mb-3">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.financial_stats_title", {defaultValue: "Přehled fakturace bez DPH"})}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.financial_stats_description", {defaultValue: "Původní měny zůstávají oddělené; souhrn Vše v CZK používá kurz ČNB k datu platby. Částky jsou bez DPH."})}</p>
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
            <div className={`rounded-2xl border border-violet-300 bg-white p-5 shadow-sm ring-1 ring-violet-100 dark:border-violet-700 dark:bg-slate-800 dark:ring-violet-900/40 ${ossStats ? "" : "xl:col-span-2"}`}>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{financialStatLabel(selectedStat, t)}</h3>
                        <Badge color="violet">{t("admin.subscription_payments.net_amount", {defaultValue: "bez DPH"})}</Badge>
                    </div>
                    <FinancialStatSwitch stats={stats} selectedStat={selectedStat} onSelect={setSelectedStatKey} t={t}/>
                </div>
                <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.current_month_invoiced", {defaultValue: "Vybráno tento měsíc"})}</dt>
                        <dd className="mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-300">{formatMinorMoney(selectedStat.currentMonthInvoicedNetAmount, selectedStat.currencyCode)}</dd>
                    </div>
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.current_month_pending", {defaultValue: "Nadcházející do konce měsíce"})}</dt>
                        <dd className="mt-1 text-2xl font-semibold text-amber-700 dark:text-amber-300">{formatMinorMoney(selectedStat.currentMonthPendingNetAmount, selectedStat.currencyCode)}</dd>
                    </div>
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.current_month_projection", {defaultValue: "Očekáváno celkem tento měsíc"})}</dt>
                        <dd className="mt-1 text-xl font-semibold text-violet-700 dark:text-violet-300">{formatMinorMoney(projectedNetAmount, selectedStat.currencyCode)}</dd>
                    </div>
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.mrr", {defaultValue: "Aktuální MRR tarifů"})}</dt>
                        <dd className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{formatMinorMoney(selectedStat.monthlyRecurringNetAmount, selectedStat.currencyCode)}</dd>
                    </div>
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.previous_month_invoiced", {defaultValue: "Vybráno minulý měsíc"})}</dt>
                        <dd className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{formatMinorMoney(selectedStat.previousMonthInvoicedNetAmount, selectedStat.currencyCode)}</dd>
                    </div>
                    <div>
                        <dt className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.month_over_month", {defaultValue: "Změna oproti minulému měsíci"})}</dt>
                        <dd className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{selectedStat.monthOverMonthChangePercentage == null
                            ? t("admin.subscription_payments.month_over_month_unavailable", {defaultValue: "—"})
                        : t("admin.subscription_payments.month_over_month_value", {defaultValue: "{{value}} %", value: new Intl.NumberFormat("cs-CZ", {maximumFractionDigits: 1, signDisplay: "always"}).format(selectedStat.monthOverMonthChangePercentage)})}</dd>
                    </div>
                </dl>
            </div>
            <OssMonitoring stats={ossStats} t={t}/>
        </div>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.revenue_trend_title", {defaultValue: "Vývoj vybraných plateb bez DPH"})}</h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.revenue_trend_description", {defaultValue: "Posledních 12 kalendářních měsíců; oranžová část ukazuje naplánované budoucí platby do konce aktuálního měsíce."})}</p>
                </div>
                <FinancialStatSwitch stats={stats} selectedStat={selectedStat} onSelect={setSelectedStatKey} t={t}/>
            </div>
            <EChart option={buildRevenueTrendChartOption(selectedStat, t)} className="mt-3 h-72"/>
        </div>
        <DataCoverageWarning stats={stats} t={t}/>
    </section>;
}

function financialStatKey(stat) {
    return stat.allCurrenciesConvertedToCzk ? "all-currencies-czk" : `currency-${stat.currencyCode}`;
}

function financialStatLabel(stat, t) {
    return stat.allCurrenciesConvertedToCzk
        ? t("admin.subscription_payments.all_currencies_czk", {defaultValue: "Vše v CZK"})
        : stat.currencyCode;
}

function DataCoverageWarning({stats, t}) {
    const rows = stats.map((stat) => ({
        currencyCode: financialStatLabel(stat, t),
        count: stat.allCurrenciesConvertedToCzk
            ? (stat.missingCzkConversionCount ?? 0)
            : (stat.currentMonthMissingHistoricalInvoiceCount ?? 0)
                + (stat.previousMonthMissingHistoricalInvoiceCount ?? 0)
                + (stat.currentMonthNonAuthoritativeLedgerCount ?? 0)
                + (stat.previousMonthNonAuthoritativeLedgerCount ?? 0),
    })).filter((row) => row.count > 0);
    if (rows.length === 0) return null;

    return <div className="mt-4 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/20 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
        <div>
            <div className="font-semibold">{t("admin.subscription_payments.coverage_warning_title", {defaultValue: "Některé částky chybí v přehledu"})}</div>
            <div className="mt-0.5">{t("admin.subscription_payments.coverage_warning_description", {defaultValue: "U některých plateb chybí spolehlivě uložený rozpad částky, proto nejsou v součtech zahrnuté."})}</div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">{rows.map((row) => <Badge key={row.currencyCode} color="yellow">{row.currencyCode}: {row.count}</Badge>)}</div>
    </div>;
}

function SubscriptionHealth({health, t}) {
    return <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.subscription_health_title", {defaultValue: "Stav předplatného"})}</h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard tone="green" label={t("admin.subscription_payments.active_paid_subscriptions", {defaultValue: "Aktivní placené tarify"})} value={health?.activePaidCount ?? 0}/>
            <SummaryCard tone="violet" label={t("admin.subscription_payments.active_trials", {defaultValue: "Aktivní zkoušky"})} value={health?.trialCount ?? 0}/>
            <SummaryCard tone="amber" label={t("admin.subscription_payments.scheduled_cancellations", {defaultValue: "Naplánovaná zrušení"})} value={health?.scheduledCancellationCount ?? 0}/>
            <SummaryCard tone="red" label={t("admin.subscription_payments.past_due_subscriptions", {defaultValue: "Po splatnosti"})} value={health?.pastDueCount ?? 0}/>
        </div>
    </section>;
}

function OssMonitoring({stats, t}) {
    if (!stats) return null;
    const percentage = stats.limitAmount > 0 ? stats.currentYearB2cNetAmount / stats.limitAmount * 100 : 0;
    const warning = stats.currentYearB2cNetAmount >= stats.warningAmount || stats.previousYearB2cNetAmount > stats.limitAmount;
    const remaining = Math.max(0, stats.limitAmount - stats.currentYearB2cNetAmount);

    return <section className={`rounded-2xl border bg-white p-5 shadow-sm dark:bg-slate-800 ${warning ? "border-amber-300 dark:border-amber-800" : "border-slate-200 dark:border-slate-700"}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.oss_title", {defaultValue: "OSS monitoring – slovenský B2C obrat"})}</h2>
                    <Badge color={warning ? "yellow" : "emerald"}>{new Intl.NumberFormat("cs-CZ", {maximumFractionDigits: 1}).format(percentage)} %</Badge>
                </div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_payments.oss_description", {defaultValue: "Počítá pouze slovenské B2C platby v EUR; reverse charge se nezahrnuje."})}</p>
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div><div className="text-slate-500">{stats.calendarYear}</div><div className="font-semibold text-slate-900 dark:text-white">{formatMinorMoney(stats.currentYearB2cNetAmount, stats.currencyCode)}</div></div>
                <div><div className="text-slate-500">{t("admin.subscription_payments.oss_remaining", {defaultValue: "Do limitu"})}</div><div className="font-semibold text-slate-900 dark:text-white">{formatMinorMoney(remaining, stats.currencyCode)}</div></div>
                <div><div className="text-slate-500">{stats.calendarYear - 1}</div><div className="font-semibold text-slate-900 dark:text-white">{formatMinorMoney(stats.previousYearB2cNetAmount, stats.currencyCode)}</div></div>
                <div><div className="text-slate-500">{t("admin.subscription_payments.oss_limit", {defaultValue: "Roční limit"})}</div><div className="font-semibold text-slate-900 dark:text-white">{formatMinorMoney(stats.limitAmount, stats.currencyCode)}</div></div>
            </div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"><div className={`h-full rounded-full ${warning ? "bg-amber-500" : "bg-emerald-500"}`} style={{width: `${Math.min(100, percentage)}%`}}/></div>
    </section>;
}

function PackageName({code, t}) {
    if (!code) return <span className="text-slate-400">—</span>;
    const translated = getTranslatedPlanName(t, code);
    return <>
        <span className="font-medium text-slate-900 dark:text-white">{translated ?? code}</span>
        {translated && <span className="ml-1 text-xs text-slate-400">({code})</span>}
    </>;
}

function PaymentStatus({status, t}) {
    if (status === "succeeded") return <Badge color="emerald">{t("admin.subscription_payments.status_succeeded", {defaultValue: "Strženo"})}</Badge>;
    if (status === "failed") return <Badge color="rose">{t("admin.subscription_payments.status_failed", {defaultValue: "Neúspěšné"})}</Badge>;
    if (status === "untracked") return <Badge color="slate">{t("admin.subscription_payments.status_untracked", {defaultValue: "Historická platba bez uloženého ID"})}</Badge>;
    if (status === "missing") return <Badge color="rose">{t("admin.subscription_payments.status_missing", {defaultValue: "Platba nevznikla"})}</Badge>;
    if (status === "pending") return <Badge color="yellow">{t("admin.subscription_payments.status_pending", {defaultValue: "Čeká na dokončení"})}</Badge>;
    return <Badge color="yellow">{status || t("admin.subscription_payments.status_unknown", {defaultValue: "Neznámý stav"})}</Badge>;
}

function InvoiceStatus({row, t}) {
    if (row.paymentStatus === "untracked") {
        return <span className="text-slate-400">{t("admin.subscription_payments.invoice_untracked", {defaultValue: "Historická faktura není evidována"})}</span>;
    }
    if (row.paymentStatus !== "succeeded") {
        return <span className="text-slate-400">{t("admin.subscription_payments.invoice_not_expected", {defaultValue: "Čeká na úspěšnou platbu"})}</span>;
    }
    if (row.invoiceStatus === "missing") {
        return <Badge color="rose">{t("admin.subscription_payments.invoice_missing", {defaultValue: "Faktura nenalezena"})}</Badge>;
    }

    return <div className="flex flex-wrap items-center gap-1.5">
        <Badge color={["paid", "generated"].includes(row.invoiceStatus) ? "emerald" : "yellow"}>
            {["paid", "generated"].includes(row.invoiceStatus)
                ? t("admin.subscription_payments.invoice_paid", {defaultValue: "Vygenerována"})
                : row.invoiceStatus}
        </Badge>
        {!row.invoiceMatchIsExact && <Badge color="slate">{t("admin.subscription_payments.match_estimated", {defaultValue: "Spárováno odhadem"})}</Badge>}
    </div>;
}

function vatReviewReason(reason, t) {
    if (reason === "unavailable") {
        return t("admin.subscription_payments.vat_review_reason_unavailable", {defaultValue: "VIES se nepodařilo ověřit"});
    }
    if (reason === "invalid") {
        return t("admin.subscription_payments.vat_review_reason_invalid", {defaultValue: "DIČ nebylo ve VIES ověřeno"});
    }

    return reason || t("admin.subscription_payments.vat_review_reason_unknown", {defaultValue: "Vyžaduje kontrolu"});
}

function PendingVatReviews({reviews, resolvingReviewId, onResolve, t}) {
    if (reviews.length === 0) return null;

    return <section id="vat-reviews" className="scroll-mt-6 rounded-2xl border border-rose-300 bg-rose-50/70 p-5 shadow-sm dark:border-rose-900/80 dark:bg-rose-950/20">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.vat_reviews_title", {defaultValue: "Čeká rozhodnutí o DPH"})}</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("admin.subscription_payments.vat_reviews_description", {defaultValue: "Dokud nerozhodnete, nevznikne faktura ani se nestrhne platba. Klientovi zůstává aktivní stávající balíček."})}</p>
            </div>
            <Badge color="rose">{t("admin.subscription_payments.vat_reviews_count", {defaultValue: "{{count}} čeká", count: reviews.length})}</Badge>
        </div>
        <div className="overflow-hidden rounded-xl border border-rose-200 bg-white dark:border-rose-900/70 dark:bg-slate-800">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                    <thead className="bg-rose-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-rose-950/30 dark:text-slate-300">
                        <tr>
                            <th className="px-4 py-3">{t("admin.subscription_payments.project", {defaultValue: "Projekt"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_payments.package_subscription", {defaultValue: "Balíček / předplatné"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_payments.vat_id", {defaultValue: "DIČ"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_payments.vat_review_reason", {defaultValue: "Důvod"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_payments.vat_review_requested_at", {defaultValue: "Od"})}</th>
                            <th className="px-4 py-3 text-right">{t("admin.subscription_payments.actions", {defaultValue: "Rozhodnutí"})}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                        {reviews.map((review) => {
                            const resolving = resolvingReviewId === review.id;
                            return <tr key={review.id} className="odd:bg-white even:bg-rose-50/30 dark:odd:bg-slate-800 dark:even:bg-rose-950/10">
                                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{review.projectName}</td>
                                <td className="px-4 py-3">
                                    <div className="min-w-64">
                                        <div className="text-base font-semibold"><PackageName code={review.packageCode} t={t}/></div>
                                        <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                            <span className="mr-2">{t("admin.subscription_payments.vat_review_net_price", {defaultValue: "Cena bez DPH"})}</span>
                                            <span className="font-semibold text-slate-900 dark:text-white">{formatMinorMoney(review.netAmount, review.currencyCode)}</span>
                                        </div>
                                        <div className="mt-1 font-mono text-[10px] text-slate-400">{review.subscriptionId ?? "—"}</div>
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3"><span className="font-mono text-xs">{review.vatNumber ?? "—"}</span><div className="mt-1 text-xs text-slate-500">{review.countryCode}</div></td>
                                <td className="px-4 py-3"><Badge color="rose">{vatReviewReason(review.verificationOutcome, t)}</Badge></td>
                                <td className="whitespace-nowrap px-4 py-3">{formatDate(review.createdAt)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex min-w-[16rem] justify-end gap-2">
                                        <Button size="small" variant="outline-warning" disabled={resolving} onClick={() => onResolve(review, true)}>{resolving ? t("admin.subscription_payments.vat_review_resolving", {defaultValue: "Ukládám…"}) : t("admin.subscription_payments.vat_review_charge_vat", {defaultValue: "Účtovat DPH"})}</Button>
                                        <Button size="small" variant="outline-violet" disabled={resolving} onClick={() => onResolve(review, false)}>{t("admin.subscription_payments.vat_review_reverse_charge", {defaultValue: "Bez DPH (reverse charge)"})}</Button>
                                    </div>
                                </td>
                            </tr>;
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    </section>;
}

export default function SubscriptionPayments() {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const configuration = useAuthorityConfiguration();
    const requestDependenciesRef = useRef({configuration, et, t});
    requestDependenciesRef.current = {configuration, et, t};
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [resolvingReviewId, setResolvingReviewId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const {configuration: currentConfiguration} = requestDependenciesRef.current;
            const nextOverview = await new AdministrationApi(currentConfiguration).listAdminSubscriptionPayments();
            setOverview(nextOverview);
            window.dispatchEvent(new CustomEvent("admin-subscription-vat-review-count-changed", {
                detail: nextOverview.pendingVatReviewCount ?? 0,
            }));
        } catch (error) {
            const {et: translateError, t: translate} = requestDependenciesRef.current;
            toast.error(await translateError(error) ?? translate("admin.subscription_payments.load_failed", {defaultValue: "Platby se nepodařilo načíst."}));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const resolveVatReview = useCallback(async (review, chargeVat) => {
        setResolvingReviewId(review.id);
        try {
            const {configuration: currentConfiguration} = requestDependenciesRef.current;
            const api = new AdministrationApi(currentConfiguration);
            await api.resolveAdminSubscriptionVATReview({
                reviewId: review.id,
                subscriptionVatReviewDecisionData: {chargeVat},
            });
            toast.success(t("admin.subscription_payments.vat_review_resolved", {defaultValue: "Rozhodnutí o DPH bylo uloženo."}));
            await load();
        } catch (error) {
            const {et: translateError, t: translate} = requestDependenciesRef.current;
            toast.error(await translateError(error) ?? translate("admin.subscription_payments.vat_review_resolve_failed", {defaultValue: "Rozhodnutí o DPH se nepodařilo uložit."}));
        } finally {
            setResolvingReviewId(null);
        }
    }, [load, t]);

    const payments = useMemo(() => (overview?.payments ?? []).filter((row) => {
        if (statusFilter !== "all" && row.paymentStatus !== statusFilter) return false;
        const needle = search.trim().toLocaleLowerCase("cs");
        return needle === "" || [
            row.projectName,
            row.packageCode,
            row.stripeChargeId,
            row.stripePaymentIntentId,
            row.stripeInvoiceId,
            row.subscriptionId,
        ].some((value) => String(value ?? "").toLocaleLowerCase("cs").includes(needle));
    }), [overview, search, statusFilter]);
    const {sortedRows: sortedPayments, sort, toggleSort} = useSortableRows(payments, "paidAt", paymentSortValue, "desc");
    const pageCount = Math.max(1, Math.ceil(sortedPayments.length / pageSize));
    const currentPage = Math.min(page, pageCount);
    const visiblePayments = useMemo(
        () => sortedPayments.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [currentPage, pageSize, sortedPayments],
    );

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, sort.key, sort.direction, pageSize]);

    return <WideSecureLayout
        title={t("admin.subscription_payments.title", {defaultValue: "Platby"})}
        actions={<Button variant="outline-primary" onClick={load} disabled={loading}>{t("admin.subscription_payments.refresh", {defaultValue: "Obnovit"})}</Button>}
    >
        <div className="space-y-6">
            <PendingVatReviews
                reviews={overview?.pendingVatReviews ?? []}
                resolvingReviewId={resolvingReviewId}
                onResolve={resolveVatReview}
                t={t}
            />

            <FinancialStats stats={overview?.financialStats ?? []} ossStats={overview?.ossStats} t={t}/>

            <SubscriptionHealth health={overview?.subscriptionHealth} t={t}/>

            <section>
                <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.operational_stats_title", {defaultValue: "Provozní stav plateb"})}</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard tone="green" label={t("admin.subscription_payments.successful", {defaultValue: "Úspěšné v načtené historii"})} value={overview?.successfulPaymentCount ?? 0}/>
                <SummaryCard tone="red" label={t("admin.subscription_payments.failed", {defaultValue: "Neúspěšné platby"})} value={overview?.failedPaymentCount ?? 0}/>
                <SummaryCard tone="amber" label={t("admin.subscription_payments.missing_invoices", {defaultValue: "Bez nalezené faktury"})} value={overview?.missingInvoiceCount ?? 0}/>
                <SummaryCard tone="violet" label={t("admin.subscription_payments.upcoming_14_days", {defaultValue: "Nadcházející do 14 dní"})} value={overview?.upcomingPaymentCount ?? 0}/>
            </div>
            </section>

            <section id="upcoming-payments" className="scroll-mt-6">
                <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.upcoming_title", {defaultValue: "Nadcházející platby v příštích 14 dnech"})}</h2>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
                                <tr><th className="px-5 py-3">{t("admin.subscription_payments.due_at", {defaultValue: "Splatnost"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.project", {defaultValue: "Projekt"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.package", {defaultValue: "Balíček"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.billing_cycle", {defaultValue: "Fakturace"})}</th><th className="px-5 py-3 text-right">{t("admin.subscription_payments.amounts", {defaultValue: "Částky"})}</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                                {(overview?.upcomingPayments ?? []).map((row) => <tr key={`${row.projectId}-${row.dueAt}`} className="odd:bg-white even:bg-slate-50/80 dark:odd:bg-slate-800 dark:even:bg-slate-900/30">
                                    <td className="whitespace-nowrap px-5 py-4">{formatDate(row.dueAt)}{row.isTrial && <div className="mt-1"><Badge color="blue">{t("admin.subscription_payments.trial", {defaultValue: "Konec zkoušky"})}</Badge></div>}</td>
                                    <td className="px-5 py-4 font-medium">{row.projectName}</td>
                                    <td className="px-5 py-4"><PackageName code={row.packageCode} t={t}/></td>
                                    <td className="px-5 py-4">{row.billingCycle === "year" ? t("admin.subscription_payments.yearly", {defaultValue: "Roční"}) : t("admin.subscription_payments.monthly", {defaultValue: "Měsíční"})}</td>
                                    <td className="whitespace-nowrap px-5 py-4"><PaymentAmounts row={row} t={t}/></td>
                                </tr>)}
                                {!loading && (overview?.upcomingPayments ?? []).length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">{t("admin.subscription_payments.no_upcoming", {defaultValue: "V příštích 14 dnech nejsou naplánované žádné platby."})}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <section id="payment-history" className="scroll-mt-6">
                <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.history_title", {defaultValue: "Historie plateb"})}</h2>
                    <div className="grid gap-2 sm:grid-cols-[minmax(18rem,28rem)_12rem]">
                        <input className="form-input w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.subscription_payments.search", {defaultValue: "Projekt, balíček nebo Stripe ID…"})}/>
                        <select className="form-select w-full" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">{t("admin.subscription_payments.all_statuses", {defaultValue: "Všechny stavy"})}</option>
                            <option value="succeeded">{t("admin.subscription_payments.status_succeeded", {defaultValue: "Strženo"})}</option>
                            <option value="failed">{t("admin.subscription_payments.status_failed", {defaultValue: "Neúspěšné"})}</option>
                            <option value="untracked">{t("admin.subscription_payments.status_untracked", {defaultValue: "Historická platba bez uloženého ID"})}</option>
                            <option value="missing">{t("admin.subscription_payments.status_missing", {defaultValue: "Platba nevznikla"})}</option>
                            <option value="pending">{t("admin.subscription_payments.status_pending", {defaultValue: "Čeká na dokončení"})}</option>
                        </select>
                    </div>
                </div>
                <LoadingComponent loading={loading}>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
                                    <tr>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.paid_at", {defaultValue: "Datum platby / očekávání"})} sortKey="paidAt" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.project", {defaultValue: "Projekt"})} sortKey="project" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.package", {defaultValue: "Balíček"})} sortKey="package" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.amounts", {defaultValue: "Částky"})} sortKey="amount" sort={sort} onSort={toggleSort} align="right"/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.payment", {defaultValue: "Platba"})} sortKey="payment" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.invoice", {defaultValue: "Faktura"})} sortKey="invoice" sort={sort} onSort={toggleSort}/>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                                    {visiblePayments.map((row) => <tr key={`${row.subscriptionId}-${row.stripePaymentIntentId ?? "missing"}`} className="transition-colors odd:bg-white even:bg-slate-50/80 hover:!bg-violet-50 dark:odd:bg-slate-800 dark:even:bg-slate-900/30">
                                        <td className="whitespace-nowrap px-5 py-4">
                                            {formatDate(row.paidAt ?? row.expectedAt)}
                                            {!row.paidAt && <div className="mt-1 text-xs text-rose-600">{t("admin.subscription_payments.expected_at", {defaultValue: "Očekávaný termín"})}</div>}
                                            <div className="mt-1 font-mono text-[11px] text-slate-400">{row.stripePaymentIntentId ?? row.subscriptionId}</div>
                                        </td>
                                        <td className="px-5 py-4">{row.projectName}</td>
                                        <td className="px-5 py-4"><PackageName code={row.packageCode} t={t}/></td>
                                        <td className="whitespace-nowrap px-5 py-4"><PaymentAmounts row={row} t={t}/></td>
                                        <td className="px-5 py-4"><PaymentStatus status={row.paymentStatus} t={t}/>{row.failureReason && <div className="mt-1 max-w-64 text-xs text-rose-600">{row.failureReason}</div>}</td>
                                        <td className="px-5 py-4"><InvoiceStatus row={row} t={t}/>{row.stripeInvoiceId && <div className="mt-1 font-mono text-[11px] text-slate-400">{row.stripeInvoiceId}</div>}</td>
                                    </tr>)}
                                    {!loading && payments.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">{t("admin.subscription_payments.empty", {defaultValue: "Filtrům neodpovídá žádná platba."})}</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-5 pb-4">
                            <TablePagination
                                page={currentPage}
                                pageSize={pageSize}
                                totalItems={sortedPayments.length}
                                onPageChange={setPage}
                                onPageSizeChange={(value) => setPageSize(value)}
                                pageSizeOptions={[10, 25, 50, 100]}
                                t={t}
                            />
                        </div>
                    </div>
                </LoadingComponent>
            </section>
        </div>
    </WideSecureLayout>;
}
